/**
 * Menu Excel Engine — Import/Export for hierarchical menu data.
 * Uses xlsx-js-style for professionally styled multi-column spreadsheets.
 *
 * Export: Generates a multi-sheet workbook with styled headers and auto-widths.
 * Import: Parses rows back into Category → Item → Option hierarchy.
 */

import * as XLSX from 'xlsx-js-style';
import type { MenuProfile, MenuCategory, MenuItem, MenuItemOption, OptionGroup } from '../types';
import { isUnassignedCat } from '../utils';

// ─── Style Constants ────────────────────────────────────────────────────────────

const HEADER_FILL   = { fgColor: { rgb: '4F46E5' } };           // indigo-600
const HEADER_FONT   = { bold: true, color: { rgb: 'FFFFFF' }, sz: 11, name: 'Calibri' };
const HEADER_BORDER = {
  top:    { style: 'thin' as const, color: { rgb: '4338CA' } },
  bottom: { style: 'thin' as const, color: { rgb: '4338CA' } },
  left:   { style: 'thin' as const, color: { rgb: '4338CA' } },
  right:  { style: 'thin' as const, color: { rgb: '4338CA' } },
};
const CAT_FILL      = { fgColor: { rgb: 'EEF2FF' } };           // indigo-50
const CAT_FONT      = { bold: true, sz: 11, name: 'Calibri', color: { rgb: '3730A3' } };
const DATA_FONT     = { sz: 10, name: 'Calibri' };
const PRICE_FMT     = '#,##0.00';
const AR_FONT       = { sz: 10, name: 'Calibri' };
const CELL_BORDER   = {
  top:    { style: 'thin' as const, color: { rgb: 'E4E4E7' } },
  bottom: { style: 'thin' as const, color: { rgb: 'E4E4E7' } },
  left:   { style: 'thin' as const, color: { rgb: 'E4E4E7' } },
  right:  { style: 'thin' as const, color: { rgb: 'E4E4E7' } },
};

// ─── Column Configuration ───────────────────────────────────────────────────────

const COLUMNS = [
  'Category (EN)', 'Category (AR)', 'Item Name (EN)', 'Item Name (AR)',
  'Description (EN)', 'Description (AR)', 'Price (QAR)',
  'Option Name (EN)', 'Option Name (AR)', 'Option Price (QAR)',
];

const STRUCTURED_TEMPLATE_COLUMNS = [
  'Row Type',
  'Category (EN)',
  'Category (AR)',
  'Item Name (EN)',
  'Item Name (AR)',
  'Description (EN)',
  'Description (AR)',
  'Price (QAR)',
  'Option Group (EN)',
  'Option Group (AR)',
  'Min',
  'Max',
  'Option Name (EN)',
  'Option Name (AR)',
  'Option Price (QAR)',
];

// ─── Export ─────────────────────────────────────────────────────────────────────

export interface ExportOptions {
  profile: MenuProfile;
  includeOptions?: boolean;
}

export function exportMenuToExcel({ profile, includeOptions = true }: ExportOptions): void {
  const wb = XLSX.utils.book_new();

  // ── Sheet 1: Full Menu Data (Structured format — round-trip safe) ──
  const EXPORT_COLUMNS = [...STRUCTURED_TEMPLATE_COLUMNS, 'Image_URL'];
  const rows: any[][] = [];

  // Header row
  rows.push(EXPORT_COLUMNS);

  for (const cat of profile.categories.filter(c => !isUnassignedCat(c.id))) {
    // CATEGORY row
    rows.push(['CATEGORY', cat.nameEN, cat.nameAR, '', '', '', '', '', '', '', '', '', '', '', '', '']);

    for (const item of cat.items) {
      // ITEM row
      rows.push(['ITEM', '', '', item.nameEN, item.nameAR, item.descriptionEN ?? '', item.descriptionAR ?? '', item.basePrice, '', '', '', '', '', '', '', item.image ? '(embedded)' : '']);

      if (includeOptions) {
        // Prefer optionGroups (hierarchical) over legacy flat options
        const groups = item.optionGroups;
        if (groups && groups.length > 0) {
          for (const grp of groups) {
            // GROUP row
            rows.push(['GROUP', '', '', '', '', '', '', '', grp.nameEN, grp.nameAR, grp.minSelection ?? 0, grp.maxSelection ?? 0, '', '', '', '']);
            for (const opt of grp.options) {
              // OPTION row
              rows.push(['OPTION', '', '', '', '', '', '', '', '', '', '', '', opt.nameEN, opt.nameAR, opt.extraPrice, '']);
            }
          }
        } else if (item.options.length > 0) {
          // Fallback: legacy flat options → export under a default group
          rows.push(['GROUP', '', '', '', '', '', '', '', 'Options', 'خيارات', 0, 0, '', '', '', '']);
          for (const opt of item.options) {
            rows.push(['OPTION', '', '', '', '', '', '', '', '', '', '', '', opt.nameEN, opt.nameAR, opt.extraPrice, '']);
          }
        }
      }
    }
  }

  const ws = XLSX.utils.aoa_to_sheet(rows);

  // Apply styles
  const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
  const colCount = EXPORT_COLUMNS.length;

  // Header styles
  for (let c = 0; c < colCount; c++) {
    const addr = XLSX.utils.encode_cell({ r: 0, c });
    if (ws[addr]) {
      ws[addr].s = {
        fill: HEADER_FILL,
        font: HEADER_FONT,
        border: HEADER_BORDER,
        alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      };
    }
  }

  // Data styles — Row Type, Arabic cols, Price cols
  const ROW_TYPE_FILL: Record<string, typeof CAT_FILL | undefined> = {
    CATEGORY: CAT_FILL,
    ITEM: undefined,
    GROUP: { fgColor: { rgb: 'F5F3FF' } },  // violet-50
    OPTION: undefined,
  };
  const ROW_TYPE_FONT: Record<string, typeof CAT_FONT | typeof DATA_FONT> = {
    CATEGORY: CAT_FONT,
    ITEM: { bold: true, sz: 10, name: 'Calibri', color: { rgb: '1E293B' } },
    GROUP: { bold: true, sz: 10, name: 'Calibri', color: { rgb: '6D28D9' } },
    OPTION: DATA_FONT,
  };

  for (let r = 1; r <= range.e.r; r++) {
    const rowType = String(ws[XLSX.utils.encode_cell({ r, c: 0 })]?.v || '').toUpperCase();

    for (let c = 0; c < colCount; c++) {
      const addr = XLSX.utils.encode_cell({ r, c });
      if (!ws[addr]) ws[addr] = { v: '', t: 's' };
      const isArabicCol = c === 2 || c === 4 || c === 6 || c === 9 || c === 13;
      const isPriceCol = c === 7 || c === 14;

      ws[addr].s = {
        font: c === 0 ? (ROW_TYPE_FONT[rowType] || DATA_FONT) : (isArabicCol ? AR_FONT : DATA_FONT),
        fill: ROW_TYPE_FILL[rowType],
        border: CELL_BORDER,
        alignment: {
          horizontal: isArabicCol ? 'right' : (isPriceCol ? 'right' : 'left'),
          vertical: 'center',
          wrapText: true,
        },
        numFmt: isPriceCol && typeof ws[addr].v === 'number' ? PRICE_FMT : undefined,
      };
    }
  }

  // Column widths
  ws['!cols'] = [
    { wch: 12 }, // Row Type
    { wch: 20 }, { wch: 18 }, // Category EN/AR
    { wch: 25 }, { wch: 22 }, // Item EN/AR
    { wch: 30 }, { wch: 25 }, // Description EN/AR
    { wch: 12 }, // Price
    { wch: 22 }, { wch: 18 }, // Option Group EN/AR
    { wch: 6 },  { wch: 6 },  // Min/Max
    { wch: 20 }, { wch: 18 }, // Option EN/AR
    { wch: 14 }, // Option Price
    { wch: 18 }, // Image_URL
  ];

  // Freeze header row
  ws['!freeze'] = { xSplit: 0, ySplit: 1, topLeftCell: 'A2', activePane: 'bottomLeft' };

  XLSX.utils.book_append_sheet(wb, ws, 'Menu Items');

  // ── Sheet 2: Profile Summary ──
  const summaryRows: any[][] = [
    ['Menu Profile Summary'],
    [],
    ['Profile Name', profile.name],
    ['Brand', profile.brandName],
    ['Type', profile.type],
    ['Status', profile.status],
    ['Total Categories', profile.categories.filter(c => !isUnassignedCat(c.id)).length],
    ['Total Items', profile.categories.filter(c => !isUnassignedCat(c.id)).reduce((a, c) => a + c.items.length, 0)],
    ['Total Options', profile.categories.filter(c => !isUnassignedCat(c.id)).reduce((a, c) => a + c.items.reduce((b, it) => b + (it.optionGroups || []).reduce((g, og) => g + og.options.length, 0) + ((!it.optionGroups || it.optionGroups.length === 0) ? it.options.length : 0), 0), 0)],
    ['Total Option Groups', profile.categories.filter(c => !isUnassignedCat(c.id)).reduce((a, c) => a + c.items.reduce((b, it) => b + (it.optionGroups || []).length, 0), 0)],
    ['Created By', profile.createdByName],
    ['Last Updated', new Date(profile.updatedAt).toLocaleDateString()],
  ];

  const sumWs = XLSX.utils.aoa_to_sheet(summaryRows);
  // Title style
  const titleAddr = XLSX.utils.encode_cell({ r: 0, c: 0 });
  if (sumWs[titleAddr]) {
    sumWs[titleAddr].s = {
      font: { bold: true, sz: 14, color: { rgb: '4F46E5' }, name: 'Calibri' },
    };
  }
  // Label styles
  for (let r = 2; r < summaryRows.length; r++) {
    const lAddr = XLSX.utils.encode_cell({ r, c: 0 });
    if (sumWs[lAddr]) {
      sumWs[lAddr].s = { font: { bold: true, sz: 10, name: 'Calibri', color: { rgb: '71717A' } } };
    }
    const vAddr = XLSX.utils.encode_cell({ r, c: 1 });
    if (sumWs[vAddr]) {
      sumWs[vAddr].s = { font: { sz: 10, name: 'Calibri' } };
    }
  }
  sumWs['!cols'] = [{ wch: 20 }, { wch: 35 }];
  XLSX.utils.book_append_sheet(wb, sumWs, 'Profile Summary');

  // Write
  const fileName = `Menu_${profile.brandName.replace(/[^a-zA-Z0-9]/g, '_')}_${profile.name.replace(/[^a-zA-Z0-9]/g, '_')}.xlsx`;
  XLSX.writeFile(wb, fileName);
}

// ─── Bulk Export: Generate Excel as ArrayBuffer (for ZIP bundling) ───────────

const _sanitizeName = (name: string) =>
  name.replace(/[\/\\:*?"<>|]/g, '').trim().replace(/\s+/g, ' ');

/**
 * Generate the structured-format Excel as an ArrayBuffer (for embedding in a ZIP).
 * When `withImageFilenames` is true, Image_URL column contains the relative path
 * matching the ZIP image folder structure: `images/<Category>/<ItemName>.<ext>`
 * instead of `(embedded)`.
 */
export function exportMenuToExcelBuffer({
  profile,
  includeOptions = true,
  withImageFilenames = false,
}: ExportOptions & { withImageFilenames?: boolean }): ArrayBuffer {
  const wb = XLSX.utils.book_new();
  const EXPORT_COLUMNS = [...STRUCTURED_TEMPLATE_COLUMNS, 'Image_URL'];
  const rows: any[][] = [];
  rows.push(EXPORT_COLUMNS);

  for (const cat of profile.categories.filter(c => !isUnassignedCat(c.id))) {
    const catName = _sanitizeName(cat.nameEN || cat.nameAR || 'Untitled');
    rows.push(['CATEGORY', cat.nameEN, cat.nameAR, '', '', '', '', '', '', '', '', '', '', '', '', '']);

    for (const item of cat.items) {
      let imageRef = '';
      if (item.image) {
        if (withImageFilenames) {
          const itemName = _sanitizeName(item.nameEN || item.nameAR || 'Item');
          imageRef = `images/${catName}/${itemName}`;
        } else {
          imageRef = '(embedded)';
        }
      }
      rows.push(['ITEM', '', '', item.nameEN, item.nameAR, item.descriptionEN ?? '', item.descriptionAR ?? '', item.basePrice, '', '', '', '', '', '', '', imageRef]);

      if (includeOptions) {
        const groups = item.optionGroups;
        if (groups && groups.length > 0) {
          for (const grp of groups) {
            rows.push(['GROUP', '', '', '', '', '', '', '', grp.nameEN, grp.nameAR, grp.minSelection ?? 0, grp.maxSelection ?? 0, '', '', '', '']);
            for (const opt of grp.options) {
              rows.push(['OPTION', '', '', '', '', '', '', '', '', '', '', '', opt.nameEN, opt.nameAR, opt.extraPrice, '']);
            }
          }
        } else if (item.options.length > 0) {
          rows.push(['GROUP', '', '', '', '', '', '', '', 'Options', 'خيارات', 0, 0, '', '', '', '']);
          for (const opt of item.options) {
            rows.push(['OPTION', '', '', '', '', '', '', '', '', '', '', '', opt.nameEN, opt.nameAR, opt.extraPrice, '']);
          }
        }
      }
    }
  }

  const ws = XLSX.utils.aoa_to_sheet(rows);
  const colCount = EXPORT_COLUMNS.length;
  for (let c = 0; c < colCount; c++) {
    const addr = XLSX.utils.encode_cell({ r: 0, c });
    if (ws[addr]) {
      ws[addr].s = {
        fill: HEADER_FILL, font: HEADER_FONT, border: HEADER_BORDER,
        alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      };
    }
  }
  ws['!cols'] = [
    { wch: 12 }, { wch: 20 }, { wch: 18 }, { wch: 25 }, { wch: 22 },
    { wch: 30 }, { wch: 25 }, { wch: 12 }, { wch: 22 }, { wch: 18 },
    { wch: 6 }, { wch: 6 }, { wch: 20 }, { wch: 18 }, { wch: 14 }, { wch: 26 },
  ];

  XLSX.utils.book_append_sheet(wb, ws, 'Menu Items');
  return XLSX.write(wb, { type: 'array', bookType: 'xlsx' }) as ArrayBuffer;
}

// ─── Export Blank Template ──────────────────────────────────────────────────────

export function exportBlankTemplate(): void {
  const wb = XLSX.utils.book_new();
  const rows: any[][] = [
    STRUCTURED_TEMPLATE_COLUMNS,
    ['CATEGORY', 'Burgers', 'برجر', '', '', '', '', '', '', '', '', '', '', '', ''],
    ['ITEM', '', '', 'Classic Burger', 'برجر كلاسيك', 'Juicy beef patty', 'قطعة لحم مشوية مع خبز طازج', 45, '', '', '', '', '', '', ''],
    ['GROUP', '', '', '', '', '', '', '', 'Choose your extras', 'اختر الإضافات', 0, 3, '', '', ''],
    ['OPTION', '', '', '', '', '', '', '', '', '', '', '', 'Extra Cheese', 'جبنة إضافية', 5],
    ['OPTION', '', '', '', '', '', '', '', '', '', '', '', 'Beef Bacon', 'بيف بيكون', 7],
    ['OPTION', '', '', '', '', '', '', '', '', '', '', '', 'Jalapeno', 'هالبينو', 2],
  ];
  const ws = XLSX.utils.aoa_to_sheet(rows);

  // Header styles
  for (let c = 0; c < STRUCTURED_TEMPLATE_COLUMNS.length; c++) {
    const addr = XLSX.utils.encode_cell({ r: 0, c });
    if (ws[addr]) {
      ws[addr].s = {
        fill: HEADER_FILL,
        font: HEADER_FONT,
        border: HEADER_BORDER,
        alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      };
    }
  }

  for (let r = 1; r < rows.length; r++) {
    const typeAddr = XLSX.utils.encode_cell({ r, c: 0 });
    if (ws[typeAddr]) {
      ws[typeAddr].s = {
        font: { bold: true, sz: 10, name: 'Calibri', color: { rgb: '0F172A' } },
        fill: { fgColor: { rgb: 'E2E8F0' } },
        border: CELL_BORDER,
        alignment: { horizontal: 'center', vertical: 'center' },
      };
    }
  }

  ws['!cols'] = [
    { wch: 14 },
    { wch: 18 }, { wch: 18 },
    { wch: 24 }, { wch: 24 },
    { wch: 34 }, { wch: 34 },
    { wch: 12 },
    { wch: 22 }, { wch: 22 },
    { wch: 8 }, { wch: 8 },
    { wch: 22 }, { wch: 22 },
    { wch: 14 },
  ];
  ws['!freeze'] = { xSplit: 0, ySplit: 1, topLeftCell: 'A2', activePane: 'bottomLeft' };
  XLSX.utils.book_append_sheet(wb, ws, 'Import Template');

  const instructionRows = [
    ['How to use this import template'],
    ['1. Use CATEGORY rows to start a category.'],
    ['2. Add one ITEM row for each product with the description and base price.'],
    ['3. Add a GROUP row for each option group and set Min / Max if needed.'],
    ['4. Add one OPTION row for every option inside the current group.'],
    ['5. Blank category or item cells inherit the current category and product context.'],
    ['6. You can also import Export Template 1 and older flat menu sheets.'],
  ];
  const instructionSheet = XLSX.utils.aoa_to_sheet(instructionRows);
  instructionSheet['!cols'] = [{ wch: 90 }];
  const titleAddr = XLSX.utils.encode_cell({ r: 0, c: 0 });
  if (instructionSheet[titleAddr]) {
    instructionSheet[titleAddr].s = {
      font: { bold: true, sz: 14, name: 'Calibri', color: { rgb: '4F46E5' } },
    };
  }
  XLSX.utils.book_append_sheet(wb, instructionSheet, 'Instructions');
  XLSX.writeFile(wb, 'Menu_Import_Template.xlsx');
}

// ─── Import ─────────────────────────────────────────────────────────────────────

export type ImportFormat = 'structured' | 'template1' | 'template2' | 'relational' | 'legacy';

export interface ImportResult {
  success: boolean;
  detectedFormat: ImportFormat;
  rowsProcessed: number;
  categoriesCreated: number;
  categoriesUpdated: number;
  itemsCreated: number;
  itemsUpdated: number;
  groupsCreated: number;
  optionsCreated: number;
  errors: string[];
  /** Map of item ID → Image_URL column value (for bulk ZIP import image matching) */
  imageFilenames: Map<string, string>;
}

function genId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function emptyReport(detectedFormat: ImportFormat): ImportResult {
  return {
    success: true,
    detectedFormat,
    rowsProcessed: 0,
    categoriesCreated: 0,
    categoriesUpdated: 0,
    itemsCreated: 0,
    itemsUpdated: 0,
    groupsCreated: 0,
    optionsCreated: 0,
    errors: [],
    imageFilenames: new Map(),
  };
}

function normalizeCell(value: unknown): string {
  return String(value ?? '').trim();
}

function normalizeKey(value: string): string {
  return value.trim().toLowerCase();
}

function parseNumericCell(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;

  const raw = normalizeCell(value);
  if (!raw || raw === '-') return null;

  const cleaned = raw.replace(/[^0-9.-]/g, '');
  if (!cleaned || cleaned === '-' || cleaned === '.' || cleaned === '-.') return null;

  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
}

function hasRowContent(row: unknown[]): boolean {
  return row.some((cell) => normalizeCell(cell) !== '');
}

function findNamedRecord<T extends { nameEN: string; nameAR: string }>(records: T[], nameEN: string, nameAR: string): T | undefined {
  const normalizedEN = normalizeKey(nameEN);
  const normalizedAR = normalizeKey(nameAR);
  return records.find((record) => {
    const matchesEN = normalizedEN && normalizeKey(record.nameEN ?? '') === normalizedEN;
    const matchesAR = normalizedAR && normalizeKey(record.nameAR ?? '') === normalizedAR;
    return !!matchesEN || !!matchesAR;
  });
}

function cloneCategories(existingCategories: MenuCategory[]): MenuCategory[] {
  return existingCategories.map((category) => ({
    ...category,
    items: category.items.map((item) => {
      const clonedLegacyOptions = (item.options || []).map((option) => ({ ...option }));
      const clonedGroups = (item.optionGroups || []).map((group) => ({
        ...group,
        options: group.options.map((option) => ({ ...option })),
      }));

      const normalizedGroups = clonedGroups.length > 0
        ? clonedGroups
        : (clonedLegacyOptions.length > 0
            ? [{
                id: genId(),
                nameEN: 'Options',
                nameAR: 'خيارات',
                options: clonedLegacyOptions,
                minSelection: 0,
                maxSelection: 0,
              }]
            : []);

      return {
        ...item,
        options: [],
        optionGroups: normalizedGroups,
      };
    }),
  }));
}

function markUpdated(set: Set<string>, id: string, increment: () => void) {
  if (set.has(id)) return;
  set.add(id);
  increment();
}

function ensureCategory(
  categories: MenuCategory[],
  nameEN: string,
  nameAR: string,
  report: ImportResult,
  updatedCategoryIds: Set<string>,
): MenuCategory {
  const resolvedNameEN = nameEN || nameAR || 'Untitled Category';
  const resolvedNameAR = nameAR || '';

  const existing = findNamedRecord(categories, resolvedNameEN, resolvedNameAR);
  if (existing) {
    let changed = false;
    if (!existing.nameEN && resolvedNameEN) { existing.nameEN = resolvedNameEN; changed = true; }
    if (!existing.nameAR && resolvedNameAR) { existing.nameAR = resolvedNameAR; changed = true; }
    if (changed) markUpdated(updatedCategoryIds, existing.id, () => { report.categoriesUpdated++; });
    return existing;
  }

  const created: MenuCategory = {
    id: genId(),
    nameEN: resolvedNameEN,
    nameAR: resolvedNameAR,
    items: [],
    sortOrder: categories.length,
  };
  categories.push(created);
  report.categoriesCreated++;
  return created;
}

function ensureItem(
  category: MenuCategory,
  data: { nameEN: string; nameAR: string; descriptionEN?: string; descriptionAR?: string; price?: number | null },
  report: ImportResult,
  updatedItemIds: Set<string>,
): MenuItem {
  const resolvedNameEN = data.nameEN || data.nameAR || 'Untitled Item';
  const resolvedNameAR = data.nameAR || '';
  const existing = findNamedRecord(category.items, resolvedNameEN, resolvedNameAR);

  if (existing) {
    let changed = false;
    if (!existing.nameEN && resolvedNameEN) { existing.nameEN = resolvedNameEN; changed = true; }
    if (!existing.nameAR && resolvedNameAR) { existing.nameAR = resolvedNameAR; changed = true; }
    if (data.descriptionEN && existing.descriptionEN !== data.descriptionEN) { existing.descriptionEN = data.descriptionEN; changed = true; }
    if (data.descriptionAR && existing.descriptionAR !== data.descriptionAR) { existing.descriptionAR = data.descriptionAR; changed = true; }
    if (data.price !== null && data.price !== undefined && existing.basePrice !== data.price) { existing.basePrice = data.price; changed = true; }
    if (changed) markUpdated(updatedItemIds, existing.id, () => { report.itemsUpdated++; });
    if (!existing.optionGroups) existing.optionGroups = [];
    existing.options = [];
    return existing;
  }

  const created: MenuItem = {
    id: genId(),
    nameEN: resolvedNameEN,
    nameAR: resolvedNameAR,
    descriptionEN: data.descriptionEN || undefined,
    descriptionAR: data.descriptionAR || undefined,
    basePrice: data.price ?? 0,
    image: undefined,
    options: [],
    optionGroups: [],
    sortOrder: category.items.length,
  };
  category.items.push(created);
  report.itemsCreated++;
  return created;
}

function ensureGroup(
  item: MenuItem,
  data: { nameEN: string; nameAR: string; minSelection?: number | null; maxSelection?: number | null },
  report: ImportResult,
): OptionGroup {
  if (!item.optionGroups) item.optionGroups = [];

  const resolvedNameEN = data.nameEN || data.nameAR || 'Options';
  const resolvedNameAR = data.nameAR || (resolvedNameEN === 'Options' ? 'خيارات' : '');
  const existing = findNamedRecord(item.optionGroups, resolvedNameEN, resolvedNameAR);

  if (existing) {
    if (!existing.nameEN && resolvedNameEN) existing.nameEN = resolvedNameEN;
    if (!existing.nameAR && resolvedNameAR) existing.nameAR = resolvedNameAR;
    if (data.minSelection !== null && data.minSelection !== undefined) existing.minSelection = data.minSelection;
    if (data.maxSelection !== null && data.maxSelection !== undefined) existing.maxSelection = data.maxSelection;
    return existing;
  }

  const created: OptionGroup = {
    id: genId(),
    nameEN: resolvedNameEN,
    nameAR: resolvedNameAR,
    options: [],
    minSelection: data.minSelection ?? 0,
    maxSelection: data.maxSelection ?? 0,
  };
  item.optionGroups.push(created);
  report.groupsCreated++;
  return created;
}

function ensureOption(
  group: OptionGroup,
  data: { nameEN: string; nameAR: string; extraPrice?: number | null },
  report: ImportResult,
): MenuItemOption | null {
  const resolvedNameEN = data.nameEN || data.nameAR || '';
  const resolvedNameAR = data.nameAR || '';
  if (!resolvedNameEN && !resolvedNameAR) return null;

  const existing = findNamedRecord(group.options, resolvedNameEN, resolvedNameAR);
  if (existing) {
    if (!existing.nameEN && resolvedNameEN) existing.nameEN = resolvedNameEN;
    if (!existing.nameAR && resolvedNameAR) existing.nameAR = resolvedNameAR;
    if (data.extraPrice !== null && data.extraPrice !== undefined) existing.extraPrice = data.extraPrice;
    return existing;
  }

  const created: MenuItemOption = {
    id: genId(),
    nameEN: resolvedNameEN,
    nameAR: resolvedNameAR,
    extraPrice: data.extraPrice ?? 0,
  };
  group.options.push(created);
  report.optionsCreated++;
  return created;
}

function detectImportFormat(rawRows: unknown[][], sheetNames?: string[]): { format: ImportFormat; headerRowIndex: number } {
  // If the workbook has a "Shared Modifier Library" sheet, it's a relational export
  const hasModifierSheet = sheetNames?.some(n => n.toLowerCase().includes('shared modifier')) ?? false;

  for (let rowIndex = 0; rowIndex < Math.min(rawRows.length, 6); rowIndex++) {
    const header = rawRows[rowIndex].map((cell) => normalizeCell(cell).toLowerCase());
    if (header.includes('row type') && header.some((cell) => cell.includes('option group'))) {
      return { format: 'structured', headerRowIndex: rowIndex };
    }
    if (header.includes('category - english') && header.includes('add-ons english')) {
      return { format: 'template2', headerRowIndex: rowIndex };
    }
    if (header.includes('category - en') && header.includes('item name - en') && header.includes('max') && header.includes('min')) {
      // Distinguish relational (has Shared Modifier Library sheet) from plain template1
      if (hasModifierSheet) {
        return { format: 'relational', headerRowIndex: rowIndex };
      }
      return { format: 'template1', headerRowIndex: rowIndex };
    }
    if (header.includes('category (en)') && header.includes('option name (en)')) {
      return { format: 'legacy', headerRowIndex: rowIndex };
    }
  }

  // Even without matching headers, if it has the modifier sheet, treat as relational
  if (hasModifierSheet) {
    return { format: 'relational', headerRowIndex: 0 };
  }

  return { format: 'legacy', headerRowIndex: 0 };
}

function importStructuredRows(dataRows: unknown[][], categories: MenuCategory[], report: ImportResult, headerRowIndex: number) {
  const updatedCategoryIds = new Set<string>();
  const updatedItemIds = new Set<string>();
  let currentCategory: MenuCategory | null = null;
  let currentItem: MenuItem | null = null;
  let currentGroup: OptionGroup | null = null;

  for (let index = 0; index < dataRows.length; index++) {
    const row = dataRows[index];
    const rowNumber = headerRowIndex + index + 2;
    const rowType = normalizeCell(row[0]).toUpperCase();
    const categoryEN = normalizeCell(row[1]);
    const categoryAR = normalizeCell(row[2]);
    const itemEN = normalizeCell(row[3]);
    const itemAR = normalizeCell(row[4]);
    const descriptionEN = normalizeCell(row[5]);
    const descriptionAR = normalizeCell(row[6]);
    const price = parseNumericCell(row[7]);
    const groupEN = normalizeCell(row[8]);
    const groupAR = normalizeCell(row[9]);
    const minSelection = parseNumericCell(row[10]);
    const maxSelection = parseNumericCell(row[11]);
    const optionEN = normalizeCell(row[12]);
    const optionAR = normalizeCell(row[13]);
    const optionPrice = parseNumericCell(row[14]);
    const imageUrl = normalizeCell(row[15]);

    if (!rowType) continue;

    if (rowType === 'CATEGORY') {
      if (!categoryEN && !categoryAR) {
        report.errors.push(`Row ${rowNumber}: CATEGORY row is missing category names.`);
        continue;
      }
      currentCategory = ensureCategory(categories, categoryEN, categoryAR, report, updatedCategoryIds);
      currentItem = null;
      currentGroup = null;
      continue;
    }

    const resolvedCategoryEN = categoryEN || currentCategory?.nameEN || '';
    const resolvedCategoryAR = categoryAR || currentCategory?.nameAR || '';
    if (!resolvedCategoryEN && !resolvedCategoryAR) {
      report.errors.push(`Row ${rowNumber}: No category context found.`);
      continue;
    }
    currentCategory = ensureCategory(categories, resolvedCategoryEN, resolvedCategoryAR, report, updatedCategoryIds);

    if (rowType === 'ITEM') {
      if (!itemEN && !itemAR) {
        report.errors.push(`Row ${rowNumber}: ITEM row is missing item names.`);
        continue;
      }
      currentItem = ensureItem(currentCategory, {
        nameEN: itemEN,
        nameAR: itemAR,
        descriptionEN,
        descriptionAR,
        price,
      }, report, updatedItemIds);
      // Record image filename for bulk ZIP import matching
      if (imageUrl && imageUrl !== '(embedded)') {
        report.imageFilenames.set(currentItem.id, imageUrl);
      }
      currentGroup = null;
      continue;
    }

    if (!currentItem) {
      if (itemEN || itemAR) {
        currentItem = ensureItem(currentCategory, {
          nameEN: itemEN,
          nameAR: itemAR,
          descriptionEN,
          descriptionAR,
          price,
        }, report, updatedItemIds);
      } else {
        report.errors.push(`Row ${rowNumber}: No item context found.`);
        continue;
      }
    }

    if (rowType === 'GROUP') {
      if (!groupEN && !groupAR) {
        report.errors.push(`Row ${rowNumber}: GROUP row is missing option group names.`);
        continue;
      }
      currentGroup = ensureGroup(currentItem, {
        nameEN: groupEN,
        nameAR: groupAR,
        minSelection,
        maxSelection,
      }, report);
      continue;
    }

    if (rowType === 'OPTION') {
      if (groupEN || groupAR) {
        currentGroup = ensureGroup(currentItem, {
          nameEN: groupEN,
          nameAR: groupAR,
          minSelection,
          maxSelection,
        }, report);
      }
      if (!currentGroup) {
        currentGroup = ensureGroup(currentItem, {
          nameEN: 'Options',
          nameAR: 'خيارات',
          minSelection: 0,
          maxSelection: 0,
        }, report);
      }
      if (!optionEN && !optionAR) {
        report.errors.push(`Row ${rowNumber}: OPTION row is missing option names.`);
        continue;
      }
      ensureOption(currentGroup, { nameEN: optionEN, nameAR: optionAR, extraPrice: optionPrice }, report);
      continue;
    }

    report.errors.push(`Row ${rowNumber}: Unsupported row type "${rowType}".`);
  }
}

function importLegacyRows(dataRows: unknown[][], categories: MenuCategory[], report: ImportResult, headerRowIndex: number) {
  const updatedCategoryIds = new Set<string>();
  const updatedItemIds = new Set<string>();
  let currentCategory: MenuCategory | null = null;
  let currentItem: MenuItem | null = null;

  for (let index = 0; index < dataRows.length; index++) {
    const row = dataRows[index];
    const rowNumber = headerRowIndex + index + 2;
    const categoryEN = normalizeCell(row[0]);
    const categoryAR = normalizeCell(row[1]);
    const itemEN = normalizeCell(row[2]);
    const itemAR = normalizeCell(row[3]);
    const descriptionEN = normalizeCell(row[4]);
    const descriptionAR = normalizeCell(row[5]);
    const price = parseNumericCell(row[6]);
    const optionEN = normalizeCell(row[7]);
    const optionAR = normalizeCell(row[8]);
    const optionPrice = parseNumericCell(row[9]);

    if (categoryEN || categoryAR) {
      currentCategory = ensureCategory(categories, categoryEN, categoryAR, report, updatedCategoryIds);
    }
    if (!currentCategory) {
      report.errors.push(`Row ${rowNumber}: No category name found.`);
      continue;
    }

    if (itemEN || itemAR) {
      currentItem = ensureItem(currentCategory, {
        nameEN: itemEN,
        nameAR: itemAR,
        descriptionEN,
        descriptionAR,
        price,
      }, report, updatedItemIds);
    } else if (currentItem && (descriptionEN || descriptionAR || price !== null)) {
      currentItem = ensureItem(currentCategory, {
        nameEN: currentItem.nameEN,
        nameAR: currentItem.nameAR,
        descriptionEN,
        descriptionAR,
        price,
      }, report, updatedItemIds);
    }

    if (!currentItem) {
      if (optionEN || optionAR) report.errors.push(`Row ${rowNumber}: Option found without an item.`);
      continue;
    }

    if (optionEN || optionAR) {
      const defaultGroup = ensureGroup(currentItem, {
        nameEN: 'Options',
        nameAR: 'خيارات',
        minSelection: 0,
        maxSelection: 0,
      }, report);
      ensureOption(defaultGroup, { nameEN: optionEN, nameAR: optionAR, extraPrice: optionPrice }, report);
    }
  }
}

// ─── Shared Modifier Library Parser ─────────────────────────────────────────

interface ParsedSharedGroup {
  nameEN: string;
  nameAR: string;
  min: number;
  max: number;
  options: { nameEN: string; nameAR: string; extraPrice: number }[];
}

/**
 * Parses the "Shared Modifier Library" sheet (Sheet 2) into a name-keyed map.
 * Sheet columns: Group Name (EN) | Group Name (AR) | Min | Max | Option Name (EN) | Option Name (AR) | Option Price
 * Groups are vertically merged — first row carries group name, subsequent rows carry options only.
 */
function parseSharedModifierSheet(ws: XLSX.WorkSheet): Map<string, ParsedSharedGroup> {
  const rawRows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' }) as unknown[][];
  const map = new Map<string, ParsedSharedGroup>();
  if (rawRows.length < 2) return map;

  let currentGroup: ParsedSharedGroup | null = null;

  for (let i = 1; i < rawRows.length; i++) {
    const row = rawRows[i];
    if (!hasRowContent(row)) continue;

    const groupEN = normalizeCell(row[0]);
    const groupAR = normalizeCell(row[1]);
    const minVal = parseNumericCell(row[2]);
    const maxVal = parseNumericCell(row[3]);
    const optEN = normalizeCell(row[4]);
    const optAR = normalizeCell(row[5]);
    const optPrice = parseNumericCell(row[6]);

    // If group name is present, start a new group
    if (groupEN || groupAR) {
      currentGroup = {
        nameEN: groupEN || groupAR,
        nameAR: groupAR || '',
        min: minVal ?? 0,
        max: maxVal ?? 0,
        options: [],
      };
      const key = normalizeKey(currentGroup.nameEN);
      map.set(key, currentGroup);
    }

    // Add option to current group
    if (currentGroup && (optEN || optAR)) {
      currentGroup.options.push({
        nameEN: optEN || optAR,
        nameAR: optAR || '',
        extraPrice: optPrice ?? 0,
      });
    }
  }

  return map;
}

// ─── Relational Import (Template 1 + Shared Modifier Library) ───────────────

/**
 * Imports a relational two-sheet workbook.
 * 1. Parses Sheet 2 ("Shared Modifier Library") to build shared group definitions.
 * 2. Parses Sheet 1 ("Approved Menu") using Template 1 column layout.
 *    - Unique groups are imported inline (same as importTemplate1Rows).
 *    - "LINKED MODIFIER GROUPS" banner rows trigger shared group attachment —
 *      each reference row resolves by name against the Sheet 2 map, and the
 *      full group (with options) is attached to the item.
 */
function importRelationalRows(
  dataRows: unknown[][],
  categories: MenuCategory[],
  report: ImportResult,
  headerRowIndex: number,
  sharedGroupMap: Map<string, ParsedSharedGroup>,
) {
  const updatedCategoryIds = new Set<string>();
  const updatedItemIds = new Set<string>();
  let currentCategory: MenuCategory | null = null;
  let currentItem: MenuItem | null = null;
  let currentGroup: OptionGroup | null = null;
  let inSharedSection = false;

  for (let index = 0; index < dataRows.length; index++) {
    const row = dataRows[index];
    const rowNumber = headerRowIndex + index + 2;
    const categoryEN = normalizeCell(row[0]);
    const categoryAR = normalizeCell(row[1]);
    const itemEN = normalizeCell(row[2]);
    const itemAR = normalizeCell(row[3]);
    const valueEN = normalizeCell(row[4]);
    const valueAR = normalizeCell(row[5]);
    const amount = parseNumericCell(row[6]);
    const maxSelection = parseNumericCell(row[7]);
    const minSelection = parseNumericCell(row[8]);
    const hasMinMax = normalizeCell(row[7]) !== '' || normalizeCell(row[8]) !== '';

    // ── Detect "LINKED MODIFIER GROUPS" banner row ──
    if (valueEN.toUpperCase() === 'LINKED MODIFIER GROUPS') {
      inSharedSection = true;
      currentGroup = null;
      continue;
    }

    // ── Category row ──
    const isCategoryRow = (categoryEN || categoryAR) && !itemEN && !itemAR && !valueEN && !valueAR && amount === null && !hasMinMax;
    if (isCategoryRow) {
      currentCategory = ensureCategory(categories, categoryEN, categoryAR, report, updatedCategoryIds);
      currentItem = null;
      currentGroup = null;
      inSharedSection = false;
      continue;
    }

    if (categoryEN || categoryAR) {
      currentCategory = ensureCategory(categories, categoryEN, categoryAR, report, updatedCategoryIds);
    }
    if (!currentCategory) {
      report.errors.push(`Row ${rowNumber}: No category context found.`);
      continue;
    }

    // ── Item row ──
    if (itemEN || itemAR) {
      currentItem = ensureItem(currentCategory, {
        nameEN: itemEN,
        nameAR: itemAR,
        descriptionEN: valueEN,
        descriptionAR: valueAR,
        price: amount,
      }, report, updatedItemIds);
      currentGroup = null;
      inSharedSection = false;
      continue;
    }

    if (!currentItem) {
      report.errors.push(`Row ${rowNumber}: No item context found.`);
      continue;
    }

    // ── Shared group reference row ──
    if (inSharedSection && (valueEN || valueAR)) {
      const lookupKey = normalizeKey(valueEN || valueAR);
      const sharedDef = sharedGroupMap.get(lookupKey);

      if (sharedDef) {
        const grp = ensureGroup(currentItem, {
          nameEN: sharedDef.nameEN,
          nameAR: sharedDef.nameAR,
          minSelection: sharedDef.min,
          maxSelection: sharedDef.max,
        }, report);
        for (const opt of sharedDef.options) {
          ensureOption(grp, { nameEN: opt.nameEN, nameAR: opt.nameAR, extraPrice: opt.extraPrice }, report);
        }
      } else {
        ensureGroup(currentItem, {
          nameEN: valueEN || valueAR,
          nameAR: valueAR || '',
          minSelection: minSelection,
          maxSelection: maxSelection,
        }, report);
        report.errors.push(`Row ${rowNumber}: Shared group "${valueEN || valueAR}" not found in Shared Modifier Library sheet.`);
      }
      continue;
    }

    // ── Group header row (has min/max) ──
    if (hasMinMax) {
      inSharedSection = false;
      if (!valueEN && !valueAR) {
        report.errors.push(`Row ${rowNumber}: Option group row is missing a group name.`);
        continue;
      }
      currentGroup = ensureGroup(currentItem, {
        nameEN: valueEN,
        nameAR: valueAR,
        minSelection,
        maxSelection,
      }, report);
      continue;
    }

    // ── Option row ──
    if (valueEN || valueAR) {
      if (!currentGroup) {
        currentGroup = ensureGroup(currentItem, {
          nameEN: 'Options',
          nameAR: 'خيارات',
          minSelection: 0,
          maxSelection: 0,
        }, report);
      }
      ensureOption(currentGroup, {
        nameEN: valueEN,
        nameAR: valueAR,
        extraPrice: amount,
      }, report);
    }
  }
}

function importTemplate1Rows(dataRows: unknown[][], categories: MenuCategory[], report: ImportResult, headerRowIndex: number) {
  const updatedCategoryIds = new Set<string>();
  const updatedItemIds = new Set<string>();
  let currentCategory: MenuCategory | null = null;
  let currentItem: MenuItem | null = null;
  let currentGroup: OptionGroup | null = null;

  for (let index = 0; index < dataRows.length; index++) {
    const row = dataRows[index];
    const rowNumber = headerRowIndex + index + 2;
    const categoryEN = normalizeCell(row[0]);
    const categoryAR = normalizeCell(row[1]);
    const itemEN = normalizeCell(row[2]);
    const itemAR = normalizeCell(row[3]);
    const valueEN = normalizeCell(row[4]);
    const valueAR = normalizeCell(row[5]);
    const amount = parseNumericCell(row[6]);
    const maxSelection = parseNumericCell(row[7]);
    const minSelection = parseNumericCell(row[8]);
    const hasMinMax = normalizeCell(row[7]) !== '' || normalizeCell(row[8]) !== '';

    const isCategoryRow = (categoryEN || categoryAR) && !itemEN && !itemAR && !valueEN && !valueAR && amount === null && !hasMinMax;
    if (isCategoryRow) {
      currentCategory = ensureCategory(categories, categoryEN, categoryAR, report, updatedCategoryIds);
      currentItem = null;
      currentGroup = null;
      continue;
    }

    if (categoryEN || categoryAR) {
      currentCategory = ensureCategory(categories, categoryEN, categoryAR, report, updatedCategoryIds);
    }
    if (!currentCategory) {
      report.errors.push(`Row ${rowNumber}: No category context found.`);
      continue;
    }

    if (itemEN || itemAR) {
      currentItem = ensureItem(currentCategory, {
        nameEN: itemEN,
        nameAR: itemAR,
        descriptionEN: valueEN,
        descriptionAR: valueAR,
        price: amount,
      }, report, updatedItemIds);
      currentGroup = null;
      continue;
    }

    if (!currentItem) {
      report.errors.push(`Row ${rowNumber}: No item context found.`);
      continue;
    }

    if (hasMinMax) {
      if (!valueEN && !valueAR) {
        report.errors.push(`Row ${rowNumber}: Option group row is missing a group name.`);
        continue;
      }
      currentGroup = ensureGroup(currentItem, {
        nameEN: valueEN,
        nameAR: valueAR,
        minSelection,
        maxSelection,
      }, report);
      continue;
    }

    if (valueEN || valueAR) {
      if (!currentGroup) {
        currentGroup = ensureGroup(currentItem, {
          nameEN: 'Options',
          nameAR: 'خيارات',
          minSelection: 0,
          maxSelection: 0,
        }, report);
      }
      ensureOption(currentGroup, {
        nameEN: valueEN,
        nameAR: valueAR,
        extraPrice: amount,
      }, report);
    }
  }
}

function parseTemplate2Block(block: string): { name: string; options: Array<{ name: string; price: number }> } | null {
  const trimmed = block.trim();
  if (!trimmed) return null;

  const separatorIndex = trimmed.indexOf('-');
  if (separatorIndex === -1) {
    return { name: trimmed, options: [] };
  }

  const name = trimmed.slice(0, separatorIndex).trim();
  const optionsText = trimmed.slice(separatorIndex + 1).trim();
  const options = optionsText
    ? optionsText.split(/\s*,\s*/).filter(Boolean).map((token) => {
        const match = token.match(/^(.*?)(?:\(([^()]*)\))?$/);
        const optionName = normalizeCell(match?.[1] ?? token);
        const optionPrice = parseNumericCell(match?.[2] ?? '') ?? 0;
        return { name: optionName, price: optionPrice };
      }).filter((option) => option.name)
    : [];

  return { name, options };
}

function parseTemplate2Groups(englishValue: string, arabicValue: string) {
  const englishBlocks = englishValue.split(/\s*\/\s*/).map(parseTemplate2Block).filter((block): block is { name: string; options: Array<{ name: string; price: number }> } => !!block);
  const arabicBlocks = arabicValue.split(/\s*\/\s*/).map(parseTemplate2Block).filter((block): block is { name: string; options: Array<{ name: string; price: number }> } => !!block);

  const groups: Array<{ nameEN: string; nameAR: string; options: Array<{ nameEN: string; nameAR: string; extraPrice: number }> }> = [];
  const totalGroups = Math.max(englishBlocks.length, arabicBlocks.length);

  for (let groupIndex = 0; groupIndex < totalGroups; groupIndex++) {
    const englishGroup = englishBlocks[groupIndex];
    const arabicGroup = arabicBlocks[groupIndex];
    const options: Array<{ nameEN: string; nameAR: string; extraPrice: number }> = [];
    const totalOptions = Math.max(englishGroup?.options.length ?? 0, arabicGroup?.options.length ?? 0);

    for (let optionIndex = 0; optionIndex < totalOptions; optionIndex++) {
      const englishOption = englishGroup?.options[optionIndex];
      const arabicOption = arabicGroup?.options[optionIndex];
      const nameEN = englishOption?.name || arabicOption?.name || '';
      const nameAR = arabicOption?.name || englishOption?.name || '';
      if (!nameEN && !nameAR) continue;
      options.push({
        nameEN,
        nameAR,
        extraPrice: englishOption?.price ?? arabicOption?.price ?? 0,
      });
    }

    const nameEN = englishGroup?.name || arabicGroup?.name || 'Options';
    const nameAR = arabicGroup?.name || englishGroup?.name || 'خيارات';
    if (!nameEN && !nameAR && options.length === 0) continue;

    groups.push({ nameEN, nameAR, options });
  }

  return groups;
}

function importTemplate2Rows(dataRows: unknown[][], categories: MenuCategory[], report: ImportResult, headerRowIndex: number) {
  const updatedCategoryIds = new Set<string>();
  const updatedItemIds = new Set<string>();

  for (let index = 0; index < dataRows.length; index++) {
    const row = dataRows[index];
    const rowNumber = headerRowIndex + index + 2;
    const categoryEN = normalizeCell(row[0]);
    const categoryAR = normalizeCell(row[1]);
    const itemEN = normalizeCell(row[2]);
    const itemAR = normalizeCell(row[3]);
    const descriptionEN = normalizeCell(row[4]);
    const descriptionAR = normalizeCell(row[5]);
    const price = parseNumericCell(row[6]);
    const addOnsEN = normalizeCell(row[8]);
    const addOnsAR = normalizeCell(row[9]);

    if (!categoryEN && !categoryAR && !itemEN && !itemAR) continue;

    const category = ensureCategory(categories, categoryEN, categoryAR, report, updatedCategoryIds);
    const item = ensureItem(category, {
      nameEN: itemEN,
      nameAR: itemAR,
      descriptionEN,
      descriptionAR,
      price,
    }, report, updatedItemIds);

    const groups = parseTemplate2Groups(addOnsEN, addOnsAR);
    for (const groupData of groups) {
      const group = ensureGroup(item, {
        nameEN: groupData.nameEN,
        nameAR: groupData.nameAR,
        minSelection: 0,
        maxSelection: 0,
      }, report);
      for (const option of groupData.options) {
        ensureOption(group, option, report);
      }
    }

    if (!itemEN && !itemAR) {
      report.errors.push(`Row ${rowNumber}: Template 2 row is missing the product name.`);
    }
  }
}

export function importMenuFromExcel(
  file: ArrayBuffer,
  existingCategories: MenuCategory[],
): { categories: MenuCategory[]; report: ImportResult } {
  const wb = XLSX.read(file, { type: 'array' });
  const ws = wb.Sheets[wb.SheetNames[0]];
  if (!ws) {
    const report = emptyReport('legacy');
    report.success = false;
    report.errors.push('No data sheet found.');
    return {
      categories: existingCategories,
      report,
    };
  }

  const rawRows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' }) as unknown[][];
  const detection = detectImportFormat(rawRows, wb.SheetNames);
  const report = emptyReport(detection.format);
  const dataRows = rawRows.slice(detection.headerRowIndex + 1).filter((row) => hasRowContent(row));
  report.rowsProcessed = dataRows.length;

  if (dataRows.length === 0) {
    report.success = false;
    report.errors.push('No import rows found in the selected workbook.');
    return { categories: existingCategories, report };
  }

  const categories = cloneCategories(existingCategories);

  switch (detection.format) {
    case 'relational': {
      const modSheetName = wb.SheetNames.find((n) => n.toLowerCase().includes('shared modifier'));
      const modSheet = modSheetName ? wb.Sheets[modSheetName] : undefined;
      const sharedGroupMap = modSheet ? parseSharedModifierSheet(modSheet) : new Map<string, ParsedSharedGroup>();
      importRelationalRows(dataRows, categories, report, detection.headerRowIndex, sharedGroupMap);
      break;
    }
    case 'structured':
      importStructuredRows(dataRows, categories, report, detection.headerRowIndex);
      break;
    case 'template1':
      importTemplate1Rows(dataRows, categories, report, detection.headerRowIndex);
      break;
    case 'template2':
      importTemplate2Rows(dataRows, categories, report, detection.headerRowIndex);
      break;
    default:
      importLegacyRows(dataRows, categories, report, detection.headerRowIndex);
      break;
  }

  report.success = report.errors.length === 0;

  return { categories, report };
}
